import { useCallback, useEffect, useState } from 'react';
import type { WorkerRecord, WorkerRecordRow } from '../lib/reports';
import { reportsApi } from '../lib/reports';
import { ApiError } from '../lib/api';
import { openPrint } from '../lib/print';
import { formatDate, formatHours, formatRs } from '../lib/money';
import WorkerStatement from '../print/WorkerStatement';
import SignDialog from '../components/SignDialog';
import { signatureApi } from '../lib/signatures';
import { ReceiptSheets } from '../print/AdvanceReceipt';
import type { AdvanceRow, EntryRow } from '../lib/day';

/**
 * Worker records.
 *
 * "Tap a name for his history — days worked, allowances, advances and what he
 * has been paid", and a printed statement.
 *
 * Every figure is read back from what was stored: approved earnings, advances
 * as taken, contributions as accrued. Nothing here recomputes a wage from the
 * worker's current salary — that is the whole point of the snapshot rule.
 */

export default function WorkerRecords({ userName, userId }: { userName: string; userId: string }) {
  const [rows, setRows] = useState<WorkerRecordRow[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await reportsApi.workerRecords();
      setRows(result.workers);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the employee records.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!rows) return <div className="card"><div className="empty">{error || 'Loading…'}</div></div>;

  return (
    <div className="card">
      <h3>Employee records</h3>
      <div className="sub">
        Tap a name for his history — days worked, allowances, advances and what he has been
        paid
      </div>

      {error && <div className="banner b-no">{error}</div>}

      {rows.map((row) => {
        // What he is owed: approved earnings not yet in a run, less the
        // advances against them.
        const owed = row.unpaid_earned_rs - row.unpaid_advances_rs;
        return (
          <div key={row.worker_id}>
            <div
              className="row tap"
              role="button"
              tabIndex={0}
              onClick={() => setOpenId(openId === row.worker_id ? null : row.worker_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setOpenId(openId === row.worker_id ? null : row.worker_id);
                }
              }}
            >
              <div style={{ flex: 1 }}>
                <div className="name">{row.worker_name}</div>
                <div className="sub">
                  {row.roles_label ?? '—'} · earned {formatRs(row.earned_rs)} to date · advances{' '}
                  {formatRs(row.advances_rs)}
                  {row.fund_credited_rs > 0 && (
                    <> · <span className="pill p-ok">EPF {formatRs(row.fund_credited_rs)}</span></>
                  )}
                </div>
              </div>
              <div className="stat">
                <div className="name">{formatRs(owed)}</div>
                <div className="sub">
                  {row.first_unpaid_date
                    ? `owed from ${formatDate(row.first_unpaid_date)}`
                    : 'all paid'}
                </div>
              </div>
            </div>

            {openId === row.worker_id && (
              <RecordDetail workerId={row.worker_id} userName={userName} userId={userId} onError={setError} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function RecordDetail({
  workerId, userName, userId, onError,
}: {
  workerId: string;
  userName: string;
  userId: string;
  onError: (message: string) => void;
}) {
  const [record, setRecord] = useState<WorkerRecord | null>(null);
  // "Print statement" first asks him to sign that he has seen it.
  const [signingStatement, setSigningStatement] = useState(false);

  useEffect(() => {
    let live = true;
    reportsApi
      .workerRecord(workerId)
      .then((result) => { if (live) setRecord(result); })
      .catch((err: unknown) =>
        onError(err instanceof Error ? err.message : 'Could not load that record.'),
      );
    return () => { live = false; };
  }, [workerId, onError]);

  if (!record) return <div className="empty">Loading…</div>;

  const approved = record.days.filter((day) => day.status === 'approved');
  const totals = approved.reduce(
    (acc, day) => ({
      days: acc.days + (day.earned_rs > 0 || day.pay_basis === 'day' ? 1 : 0),
      otTenths: acc.otTenths + day.ot_tenths,
      salary: acc.salary + day.salary_rs,
      ot: acc.ot + day.ot_rs,
      allowance: acc.allowance + day.allowance_rs + day.sunday_allowance_rs,
      gross: acc.gross + day.earned_rs,
      base: acc.base + day.base_earned_rs,
    }),
    { days: 0, otTenths: 0, salary: 0, ot: 0, allowance: 0, gross: 0, base: 0 },
  );
  const advancesTotal = record.advances.reduce((sum, a) => sum + a.amount_rs, 0);
  const credited = record.contributions.reduce((sum, c) => sum + c.epf_total_rs + c.etf_rs, 0);
  const remitted = record.contributions
    .filter((c) => c.paid_on)
    .reduce((sum, c) => sum + c.epf_total_rs + c.etf_rs, 0);
  const advancesByDate = new Map(record.advances.map((a) => [a.work_date, a.amount_rs]));

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '10px 12px', margin: '0 -4px 8px',
      }}
    >
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Job done</th>
              <th className="num">Hrs</th>
              <th className="num">OT</th>
              <th className="num">Salary</th>
              <th className="num">OT pay</th>
              <th className="num">Allowance</th>
              <th className="num">Earned</th>
              <th className="num">Base (EPF)</th>
              <th className="num">Advance</th>
              <th>Pay run</th>
            </tr>
          </thead>
          <tbody>
            {record.days.length === 0 && (
              <tr><td colSpan={11} className="empty">Nothing recorded yet.</td></tr>
            )}
            {record.days.map((day) => (
              <tr key={day.work_date}>
                <td>{formatDate(day.work_date)}</td>
                <td>
                  {day.role_name ?? '—'}
                  {day.pay_basis === 'trip' && <i> (allowance only)</i>}
                  {day.status !== 'approved' && <i> (not approved)</i>}
                  {day.allowance_reason && (
                    <div className="sub" style={{ fontSize: 11 }}>{day.allowance_reason}</div>
                  )}
                </td>
                <td className="num">
                  {day.pay_basis === 'trip' ? '—' : formatHours(day.hours_tenths)}
                </td>
                <td className="num">{day.ot_tenths ? formatHours(day.ot_tenths) : '—'}</td>
                <td className="num">{day.salary_rs ? formatRs(day.salary_rs) : '—'}</td>
                <td className="num">{day.ot_rs ? formatRs(day.ot_rs) : '—'}</td>
                <td className="num">
                  {day.allowance_rs + day.sunday_allowance_rs
                    ? formatRs(day.allowance_rs + day.sunday_allowance_rs)
                    : '—'}
                </td>
                <td className="num">{formatRs(day.earned_rs)}</td>
                <td className="num">{day.base_earned_rs ? formatRs(day.base_earned_rs) : '—'}</td>
                <td className="num">
                  {advancesByDate.has(day.work_date)
                    ? `−${formatRs(advancesByDate.get(day.work_date)!)}`
                    : '—'}
                </td>
                <td>
                  {day.pay_run_id
                    ? day.run_status === 'approved'
                      ? `paid ${formatDate(day.run_period_to!)}`
                      : 'draft run'
                    : <span style={{ color: 'var(--text-3)' }}>unpaid</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid2" style={{ marginTop: 10 }}>
        <div className="metric" style={{ background: 'var(--surface)' }}>
          <div className="k">
            {totals.days} days · {formatHours(totals.otTenths)} OT hours
          </div>
          <div className="v" style={{ fontSize: 18 }}>{formatRs(totals.gross)}</div>
          <div className="k">gross earned</div>
        </div>
        <div className="metric" style={{ background: 'var(--surface)' }}>
          <div className="k">Advances taken</div>
          <div className="v" style={{ fontSize: 18 }}>{formatRs(advancesTotal)}</div>
        </div>
      </div>

      <div className="ln" style={{ marginTop: 8 }}>
        <span className="k">
          Salary {formatRs(totals.salary)} + overtime {formatRs(totals.ot)} + allowances{' '}
          {formatRs(totals.allowance)}
        </span>
        <span>{formatRs(totals.gross)}</span>
      </div>

      {/* Hidden entirely for a non-member, and for a member with no
          contribution. Never rows of zeros. */}
      {credited > 0 ? (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--line)' }}>
          <div className="sub" style={{ marginBottom: 4 }}>
            EPF and ETF
            {record.memberships[0]?.epf_no && ` · ${record.memberships[0].epf_no}`} — on base
            salary only
            {record.memberships[0] && `, member from ${formatDate(record.memberships[0].member_from)}`}
          </div>
          <div className="ln">
            <span className="k">Base salary earned</span>
            <span>{formatRs(totals.base)}</span>
          </div>
          <div className="ln tot">
            <span>Credited to his fund</span>
            <span>{formatRs(credited)}</span>
          </div>
          <div className="ln">
            <span className="k">Paid to the fund for him</span>
            <span>{formatRs(remitted)}</span>
          </div>
          <div className="ln">
            <span className="k">Still to be paid to the fund</span>
            <span style={{ color: credited - remitted > 0 ? 'var(--text-warning)' : undefined }}>
              {formatRs(credited - remitted)}
            </span>
          </div>
          {record.contributions.filter((c) => c.paid_on).length > 0 && (
            <>
              <div className="sub" style={{ marginTop: 8 }}>Payments made for him</div>
              {record.contributions
                .filter((c) => c.paid_on)
                .map((c) => (
                  <div className="ln" key={c.pay_run_id}>
                    <span className="k">
                      {c.period_month} · paid {formatDate(c.paid_on!)}
                      {c.reference && ` · ${c.reference}`}
                    </span>
                    <span>{formatRs(c.epf_total_rs + c.etf_rs)}</span>
                  </div>
                ))}
            </>
          )}
        </div>
      ) : (
        <div className="sub" style={{ marginTop: 8 }}>Not an EPF/ETF member.</div>
      )}

      {/* "Where can I see signed receipts in records?" — here. Each advance
          he was given, with the docket it was signed on. The receipt is the
          same document the clerk printed at the time: the record carries what
          it needs (who paid, the role that day, the week's total). */}
      {record.advances.length > 0 && (
        <>
          <div className="sub" style={{ marginTop: 10 }}>Advances and receipts</div>
          {record.advances.map((advance) => (
            <div className="ln" key={advance.id} style={{ alignItems: 'center' }}>
              <span className="k">
                {formatDate(advance.work_date)}
                {advance.note && ` · ${advance.note}`}
                {' · '}
                {advance.signed
                  ? <span className="pill p-ok">signed</span>
                  : <span className="pill p-no">not signed</span>}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--text-danger)' }}>−{formatRs(advance.amount_rs)}</span>
                <button
                  type="button"
                  className="btn sm"
                  style={{ minWidth: 0 }}
                  onClick={() => {
                    // The receipt reads three things off the day's entry; the
                    // rest of EntryRow is not on a docket.
                    const entry = {
                      workerName: record.worker.name,
                      rolesLabel: advance.role_name ?? '',
                      advancesThisWeekRs: advance.week_rs,
                    } as unknown as EntryRow;
                    const row: AdvanceRow = {
                      id: advance.id,
                      amountRs: advance.amount_rs,
                      note: advance.note,
                      payWeekEnding: advance.pay_week_ending,
                      signed: !!advance.signed,
                      signedAt: advance.signed_at,
                      paidByName: advance.paid_by_name,
                      rowVersion: 0, // a docket never edits
                    };
                    openPrint(
                      `Advance receipt — ${record.worker.name} — ${formatDate(advance.work_date)}`,
                      <ReceiptSheets
                        items={[{
                          entry, advance: row,
                          approval: { by: advance.approved_by, name: advance.approved_by_name, at: advance.approved_at },
                        }]}
                        workDate={advance.work_date}
                      />,
                    );
                  }}
                >
                  Receipt
                </button>
              </span>
            </div>
          ))}
        </>
      )}

      <div className="bar" style={{ marginTop: 10 }}>
        <button type="button" className="btn" onClick={() => setSigningStatement(true)}>
          Print statement
        </button>
      </div>

      {signingStatement && (() => {
        // The dates the statement covers: his first and last approved day.
        const dates = record.days.filter((day) => day.status === 'approved').map((day) => day.work_date).sort();
        const from = dates[0] ?? null;
        const to = dates.at(-1) ?? null;
        const print = (signature: { id: string; signedAt: string } | null) => {
          setSigningStatement(false);
          const now = new Date();
          openPrint(
            `Statement — ${record.worker.name}`,
            <WorkerStatement
              record={record}
              printedBy={userName}
              printedAt={now}
              checkedBy={{ id: userId, name: userName, at: now.toISOString() }}
              signature={signature}
            />,
          );
        };
        return (
          <SignDialog
            title="Statement — signature"
            lead="Go through the statement with him, then have him sign on the tablet that he has seen it."
            summary={
              <div className="row" style={{ paddingTop: 0 }}>
                <div>
                  <div className="name">{record.worker.name}</div>
                  <div className="sub">
                    Earnings statement{from && to ? ` · ${formatDate(from)} to ${formatDate(to)}` : ''}
                  </div>
                </div>
              </div>
            }
            padLabel={`Signature of ${record.worker.name}`}
            onSave={async (png) => {
              const saved = await signatureApi.signStatement(record.worker.id, png, from, to);
              print({ id: saved.id, signedAt: saved.signedAt });
            }}
            skipLabel="Print without signing"
            onSkip={() => print(null)}
            onClose={() => setSigningStatement(false)}
          />
        );
      })()}
    </div>
  );
}
